import { z } from "zod";

export const productSchema = z
  .object({
    // Basic Info Section
    productName: z
      .string()
      .trim()
      .min(3, "Product name must be atleast 3 characters")
      .max(100, "Product name must be under 100 characters")
      .regex(/^[a-zA-Z0-9\s\-.,&'()/#+]+$/, {
        message:
          "Only letters, numbers, spaces, and these symbols are allowed: - . , & ' ( ) / # +",
      }),

    productDescription: z
      .string()
      .trim()
      .max(1000, "Description must be under 1000 characters")
      .refine(val => val === "" || val.length > 3, {
        message: "Description must be at least 4 characters if provided",
      })
      .optional(),

    productImages: z
      .array(
        z.object({
          // Backend asset id may be undefined until persisted
          id: z.string().optional(),
          url: z.string().url("Invalid image URL"),
          alt: z.string().optional(),
        })
      )
      .min(1, "At least one product image is required")
      .max(5, "You can only upload up to 5 product images"),
  

    productCategory: z.enum(["uncategorized", "perfume", "gift", "men's perfume", "unisex"], {
      errorMap: () => ({ message: "Select a valid category" }),
    }),

    productStatus: z.enum(["draft", "active", "archived"], {
      errorMap: () => ({ message: "Select a valid status" }),
    }),

    // Pricing Section
    productPrice: z
      .number({ invalid_type_error: "Price must be a number" })
      .positive("Price must be greater than zero")
      .max(10_000_000, "Price exceeds maximum allowed")
      .optional(),

    productDiscountedPrice: z
      .number({ invalid_type_error: "Discounted price must be a number" })
      .positive("Discounted price must be greater than zero")
      .max(10_000_000, "Discounted price exceeds maximum allowed")
      .optional(),

    productCostPerItem: z
      .number({ invalid_type_error: "Cost must be a number" })
      .nonnegative("Cost cannot be negative")
      .max(10_000_000, "Cost exceeds maximum allowed")
      .optional(),

    // Inventory Section
    trackInventory: z.boolean(),

    inventoryQuantity: z
      .number({ invalid_type_error: "Stock quantity must be a number" })
      .nonnegative("Stock quantity cannot be negative")
      .max(1_000_000, "Stock quantity is unrealistically high"),

    hasSKUBarcode: z.boolean().optional(),

    productSku: z.string().trim().optional(),

    productBarcode: z.string().trim().optional(),

    // Variants Section (optional for now)
    variants: z
      .array(
        z.object({
          variantOption: z
            .string()
            .trim()
            .min(1, "Option name is required")
            .max(50, "Option name must be 50 characters or less")
            .regex(/^[a-zA-Z0-9\s-]+$/, "Only letters, numbers, spaces and dashes are allowed"),
        
          variantValues: z
            .array(
              z
                .string()
                .trim()
                .min(1, "Value cannot be empty")
                .max(50, "Value must be 50 characters or less")
                .regex(/^[a-zA-Z0-9\s-]+$/, "Only letters, numbers, spaces and dashes are allowed")
            )
            .min(1, "At least one value is required")
            .refine(
              (vals) => new Set(vals.map((v) => v.trim().toLowerCase())).size === vals.length,
              "Values must be unique"
            ),
        })
      )
      .max(2, "You can only have up to 2 variant options.")
      .optional()
      .refine(
        (arr) => {
          if (!arr) return true;
          const seen = new Set();
          return arr.every((opt) => {
            const key = opt.variantOption.trim().toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        },
        { message: "Option names must be unique" }
      ),
      
  })
  .refine(
    (data) => {
      // If checkbox is checked, ensure SKU or barcode is filled
      if (data.hasSKUBarcode) {
        return Boolean(data.productSku?.trim() || data.productBarcode?.trim());
      }
      return true;
    },
    {
      message: "Provide either a SKU or Barcode if the checkbox is checked",
      path: ["productSku"], // <-- this just tells Zod *where* to attach the error
    }
  )

  .refine((data) => {
    if (data.variantOption?.length) {
      return data.variantValue?.length > 0;
    }
    return true;
  }, {
    message: "You must add at least one variant value if a variant option exists.",
    path: ["variantValue"],
  })

  .refine(
    (data) =>
      !data.productDiscountedPrice ||
      !data.productPrice ||
      data.productDiscountedPrice <= data.productPrice,
    {
      message: "Discounted price cannot be more than price",
      path: ["productDiscountedPrice"],
    }
  )
